import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { FiArrowLeft, FiBookOpen, FiChevronDown, FiDownload, FiExternalLink, FiFileText, FiFilm, FiFolder, FiHeadphones, FiRefreshCw, FiSearch } from "react-icons/fi";
import "./WorkspaceResources.css";
import WorkspaceDocumentToolbar from './WorkspaceDocumentToolbar';

const videoTypes = new Set(["mp4", "m4v", "mov", "webm", "avi", "mkv", "wmv", "mpeg", "mpg"]);
const audioTypes = new Set(["mp3", "m4a", "wav", "ogg", "oga", "aac", "flac", "aiff", "wma", "opus"]);
const documentTypes = new Set(["pdf", "doc", "docx", "word", "word_x", "txt", "rtf", "ppt", "pptx", "power_point", "power_point_x"]);
const extensionAliases = { word: "doc", word_x: "docx", power_point: "ppt", power_point_x: "pptx", excel: "xls", excel_x: "xlsx" };
const knownExtensions = new Set([...videoTypes, ...audioTypes, ...documentTypes, "csv", "xls", "xlsx", "png", "jpg", "jpeg", "gif", "webp", "svg", "zip"]);
const assetFormat = asset => {
  const extension = String(asset.downloadFileName || asset.fileName || asset.name || asset.title || "").match(/\.([a-z0-9]{1,12})$/i)?.[1];
  const format = String(extension || asset.fileType || "").toLowerCase();
  return /^[a-z0-9_]{1,24}$/.test(format) ? format : "";
};
const mediaType = asset => {
  const mime = String(asset.contentType || asset.mimeType || "").toLowerCase();
  if (mime.startsWith("audio/") || audioTypes.has(assetFormat(asset))) return "audio";
  if (mime.startsWith("video/") || videoTypes.has(assetFormat(asset))) return "video";
  return "";
};
const resourceType = asset => (mediaType(asset) || asset.type === "recording") ? "recording"
  : asset.type === "document" || documentTypes.has(assetFormat(asset)) ? "document" : "file";
const fileType = asset => assetFormat(asset) || mediaType(asset) || (asset.type === "recording" ? "link" : "file");
const fileLabels = { link: "Recording link", file: "File", video: "Video", audio: "Audio", word: "DOC", word_x: "DOCX", power_point: "PPT", power_point_x: "PPTX" };
const fileLabel = value => fileLabels[value] || value.toUpperCase();

const monthLabel = new Intl.DateTimeFormat("en", { month: "long", year: "numeric", timeZone: "UTC" });
const dateLabel = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
const recordedDate = asset => {
  const day = typeof asset.recordedAt === "string" ? asset.recordedAt.match(/^\d{4}-\d{2}-\d{2}(?=$|T)/)?.[0] : null;
  if (!day) return null;
  // Keep the recorded calendar date stable across viewer time zones.
  const date = new Date(`${day}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === day ? date : null;
};

function ResourceSection({ id, title, count, expanded, onToggle, nested = false, children }) {
  const Heading = nested ? "h3" : "h2";
  return <section className={`workspace-resources__section${nested ? " workspace-resources__section--month" : ""}`} aria-labelledby={`${id}-heading`}>
    <Heading id={`${id}-heading`} className="workspace-resources__section-heading">
      <button type="button" aria-expanded={expanded} aria-controls={`${id}-content`} onClick={onToggle}>
        <FiChevronDown aria-hidden="true" /><span>{title}</span><span className="workspace-resources__section-count">{count}</span>
      </button>
    </Heading>
    <div id={`${id}-content`} hidden={!expanded}>{children}</div>
  </section>;
}

const recordingUrl = value => {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password ? url.href : null;
  } catch { return null; }
};

export default function WorkspaceResources({ baseUrl = "", mode = "assets", project, requestJson, active = true }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [formatFilter, setFormatFilter] = useState("all");
  const [documentOpen, setDocumentOpen] = useState(mode === "documentation" && project.slug === "riomax");
  const [documentLoading, setDocumentLoading] = useState(true);
  const [pending, setPending] = useState([]);
  const [collapsed, setCollapsed] = useState({});
  const sectionId = useId();
  const search = useRef(null);
  const frame = useRef(null);
  const requests = useRef({ epoch: 0, load: null, downloads: new Map() });
  const documentationUrl = `${baseUrl}/backend/sow/content/${encodeURIComponent(project.slug)}/documentation`;
  const hasGuide = mode === "documentation" && project.slug === "riomax";

  const load = useCallback(async () => {
    requests.current.load?.abort();
    const controller = new AbortController();
    const epoch = ++requests.current.epoch;
    requests.current.load = controller;
    setLoading(true); setError("");
    try {
      const payload = await requestJson("/backend/sow/assets", { signal: controller.signal });
      if (epoch !== requests.current.epoch || controller.signal.aborted) return;
      if (!Array.isArray(payload?.data?.assets)) throw new Error("Project assets could not be loaded.");
      setAssets(payload.data.assets);
    } catch (failure) {
      if (epoch === requests.current.epoch && !controller.signal.aborted && failure.name !== "AbortError") {
        setAssets([]); setError(failure.message || "Project assets could not be loaded.");
      }
    } finally { if (epoch === requests.current.epoch) setLoading(false); }
  }, [requestJson]);

  useEffect(() => {
    const state = requests.current;
    setAssets([]); setPending([]); setDocumentOpen(hasGuide); setDocumentLoading(true); setQuery(""); setFilter("all"); setFormatFilter("all"); setCollapsed({});
    load();
    return () => {
      state.epoch++; state.load?.abort();
      state.downloads.forEach(controller => controller.abort()); state.downloads.clear();
    };
  }, [load, project.slug, hasGuide, mode]);

  useEffect(() => { setCollapsed({}); }, [query, filter, formatFilter]);

  const focusSearch = useCallback(() => {
    const input = search.current;
    if (!active || !input || input.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
    input.focus(); input.select();
    return true;
  }, [active]);

  useEffect(() => {
    if (!active || (documentOpen && hasGuide)) return undefined;
    const find = event => {
      if (event.defaultPrevented || event.target?.closest?.('[role="dialog"], [role="alertdialog"], dialog')) return;
      if (event.type === "keydown") {
        if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey || event.isComposing
          || (event.code !== "KeyK" && String(event.key).toLowerCase() !== "k")) return;
      } else if ((event.detail?.app && event.detail.app !== mode)
        || (event.detail?.projectSlug && event.detail.projectSlug !== project.slug)) return;
      if (focusSearch()) { event.preventDefault(); event.stopPropagation(); }
    };
    window.addEventListener("keydown", find, true);
    window.addEventListener("deal-desk:find", find);
    return () => { window.removeEventListener("keydown", find, true); window.removeEventListener("deal-desk:find", find); };
  }, [active, documentOpen, hasGuide, focusSearch, mode, project.slug]);

  useEffect(() => {
    const receive = event => {
      if (event.source === frame.current?.contentWindow && event.data?.type === "sow:session-expired") {
        window.dispatchEvent(new CustomEvent("sow:session-expired"));
      }
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, []);

  const download = async asset => {
    if (requests.current.downloads.has(asset.id)) return;
    const controller = new AbortController();
    requests.current.downloads.set(asset.id, controller);
    setPending([...requests.current.downloads.keys()]); setError("");
    try {
      const path = `/backend/sow/assets/${encodeURIComponent(asset.id)}/download`;
      const response = await fetch(`${baseUrl}${path}?projectSlug=${encodeURIComponent(project.slug)}`, {
        credentials: "include", cache: "no-store", signal: controller.signal, headers: { "X-SOW-Project": project.slug },
      });
      if (controller.signal.aborted) return;
      if (response.status === 401) window.dispatchEvent(new CustomEvent("sow:session-expired"));
      if (!response.ok || /text\/html|application\/json/i.test(response.headers.get("Content-Type") || "")) {
        throw new Error("The file could not be downloaded. Please retry.");
      }
      const blob = await response.blob();
      if (controller.signal.aborted) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const format = assetFormat(asset);
      const extension = extensionAliases[format] || (knownExtensions.has(format) ? format : "");
      const name = asset.fileName || asset.title || "Project file";
      link.href = url; link.download = asset.downloadFileName || (extension && !name.toLowerCase().endsWith(`.${extension}`) ? `${name}.${extension}` : name);
      document.body.appendChild(link); link.click(); link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (failure) {
      if (!controller.signal.aborted) setError(failure.message);
    } finally {
      if (!controller.signal.aborted) {
        requests.current.downloads.delete(asset.id); setPending([...requests.current.downloads.keys()]);
      }
    }
  };

  const resources = assets.filter(asset => mode !== "documentation" || resourceType(asset) === "document");
  const formats = [...new Set(resources.map(fileType))].sort((a, b) => fileLabel(a).localeCompare(fileLabel(b)));
  const categories = [
    { id: "all", label: "All assets", icon: FiFolder },
    { id: "document", label: "Documents", icon: FiFileText },
    { id: "recording", label: "Recordings", icon: FiFilm },
    ...(filter === "file" || resources.some(asset => resourceType(asset) === "file") ? [{ id: "file", label: "Other files", icon: FiFolder }] : []),
  ];
  const rows = resources.filter(asset => (filter === "all" || resourceType(asset) === filter)
    && (formatFilter === "all" || fileType(asset) === formatFilter)
    && `${asset.title} ${asset.description || ""} ${asset.provider || ""} ${fileLabel(fileType(asset))} ${mediaType(asset)} ${asset.recordedAt || ""} ${recordedDate(asset) ? monthLabel.format(recordedDate(asset)) : ""}`.toLowerCase().includes(query.trim().toLowerCase()));
  const showGuide = hasGuide && "riomax documentation administrator guide".includes(query.trim().toLowerCase());
  const resultCount = rows.length + Number(showGuide);
  const hasFilters = Boolean(query || filter !== "all" || formatFilter !== "all");
  const emptyTitle = !query && formatFilter === "all" && filter === "recording" ? "No recordings available"
    : hasFilters ? "No matching resources" : "No resources available";

  const documents = rows.filter(asset => resourceType(asset) === "document");
  const recordings = rows.filter(asset => resourceType(asset) === "recording");
  const other = rows.filter(asset => resourceType(asset) === "file");
  const months = new Map();
  recordings.forEach(asset => {
    const date = recordedDate(asset);
    const key = date ? date.toISOString().slice(0, 7) : "undated";
    if (!months.has(key)) months.set(key, { title: date ? monthLabel.format(date) : "Undated recordings", assets: [] });
    months.get(key).assets.push(asset);
  });
  const recordingMonths = [...months.entries()].sort(([a], [b]) => a === "undated" ? 1 : b === "undated" ? -1 : b.localeCompare(a));
  const sectionProps = key => ({ id: `${sectionId}-${key}`, expanded: !collapsed[key], onToggle: () => setCollapsed(value => ({ ...value, [key]: !value[key] })) });
  const renderResource = (asset, nested = false) => {
    const Heading = nested ? "h4" : "h3";
    const date = resourceType(asset) === "recording" ? recordedDate(asset) : null;
    return <article className="deal-desk__resource-row" key={asset.id}>
      <div className="deal-desk__resource-name"><span className={`deal-desk__resource-icon${resourceType(asset) === "recording" ? " deal-desk__resource-icon--recording" : ""}`}>
        {mediaType(asset) === "audio" ? <FiHeadphones aria-hidden="true" /> : resourceType(asset) === "recording" ? <FiFilm aria-hidden="true" /> : <FiFileText aria-hidden="true" />}</span>
        <div><Heading>{asset.title}</Heading><p>{asset.description || asset.provider}</p>
          {date && <time className="workspace-resources__recorded-at" dateTime={date.toISOString().slice(0, 10)}>{dateLabel.format(date)}</time>}
          {asset.type === "recording" && asset.passcode && <span className="deal-desk__passcode">Passcode <code>{asset.passcode}</code></span>}</div></div>
      <span className="deal-desk__resource-type workspace-resources__type">{mediaType(asset) === "audio" ? "Audio recording" : mediaType(asset) === "video" ? "Video recording" : resourceType(asset) === "recording" ? "Recording" : resourceType(asset) === "document" ? "Document" : "File"}
        <small>{fileLabel(fileType(asset))}</small></span>
      <div className="deal-desk__resource-actions">{asset.type === "recording"
        ? recordingUrl(asset.url) ? <a className="deal-desk__button" href={recordingUrl(asset.url)} target="_blank" rel="noreferrer"><FiExternalLink aria-hidden="true" />{asset.actionLabel === "Open record" ? "Open record" : "Watch recording"}</a> : <span className="deal-desk__access-label">Link unavailable</span>
        : <button type="button" className="deal-desk__button" disabled={pending.includes(asset.id)} onClick={() => download(asset)}>
          {pending.includes(asset.id) ? <FiRefreshCw className="sow-spin" aria-hidden="true" /> : <FiDownload aria-hidden="true" />}{pending.includes(asset.id) ? "Downloading..." : "Download"}</button>}</div>
    </article>;
  };

  if (documentOpen && hasGuide) return <section className="deal-desk__document" aria-label="Project documentation">
    <WorkspaceDocumentToolbar frame={frame} active={active} loading={documentLoading} label="Documentation sections"
      menuItems={<a className="workspace-document-tools__link" href={documentationUrl} target="_blank" rel="noreferrer"><FiExternalLink />Open in new tab</a>}>
      <button type="button" className="deal-desk__icon" aria-label="Back to documentation library" title="Back to library" onClick={() => setDocumentOpen(false)}><FiArrowLeft /></button>
    </WorkspaceDocumentToolbar>
    {documentLoading && <div className="deal-desk__loading" role="status">Opening documentation...</div>}
    <iframe ref={frame} title="Riomax documentation" src={`${documentationUrl}?workspaceApp=documentation`} onLoad={() => setDocumentLoading(false)}
      sandbox="allow-scripts allow-modals allow-downloads allow-popups allow-popups-to-escape-sandbox" referrerPolicy="no-referrer" />
  </section>;

  return <section className="deal-desk__resources" aria-label={mode === "documentation" ? "Documentation library" : "Project assets"}>
    <header className="deal-desk__library-header">
      <div className="deal-desk__library-title"><h1>{mode === "documentation" ? "Documentation" : "Assets"}</h1>
        {!loading && !error && <span className="deal-desk__count" aria-label={`${resultCount} resources`}>{resultCount}</span>}</div>
      <div className="deal-desk__library-controls">
      <label className="deal-desk__search"><FiSearch aria-hidden="true" /><input ref={search} type="search" aria-label={mode === "documentation" ? "Search documentation" : "Search assets"}
        placeholder={mode === "documentation" ? "Search documentation" : "Search assets"} value={query} onChange={event => setQuery(event.target.value)} /></label>
      <button type="button" className="deal-desk__button workspace-resources__find" title="Find (Ctrl/Cmd+K)" aria-keyshortcuts="Control+k Meta+k" onClick={focusSearch}>
        <FiSearch aria-hidden="true" />Find <kbd>Ctrl/Cmd+K</kbd>
      </button>
      {mode === "assets" && <select aria-label="File type" value={formatFilter} onChange={event => setFormatFilter(event.target.value)}>
        <option value="all">All file types</option>{formats.map(format => <option key={format} value={format}>{fileLabel(format)}</option>)}
        {formatFilter !== "all" && !formats.includes(formatFilter) && <option value={formatFilter}>{fileLabel(formatFilter)}</option>}
      </select>}
      <button type="button" className="deal-desk__icon" title="Refresh library" aria-label="Refresh library" disabled={loading} onClick={load}><FiRefreshCw className={loading ? "sow-spin" : ""} /></button>
      </div>
    </header>
    {mode === "assets" && <div className="workspace-resources__categories" role="group" aria-label="Asset type">
      {categories.map(({ id, label, icon: Icon }) => <button key={id} type="button" aria-pressed={filter === id}
        onClick={() => setFilter(id)}><Icon aria-hidden="true" />{label}
        <span>{!loading && !error ? id === "all" ? resources.length : resources.filter(asset => resourceType(asset) === id).length : ""}</span>
      </button>)}
    </div>}
    {error && <div className="deal-desk__error" role="alert">{error}<button type="button" onClick={load}>Retry</button></div>}
    <div className="deal-desk__resource-list">
    {loading ? <div className="deal-desk__skeleton" role="status" aria-label="Loading project assets"><span /><span /><span /></div> : <>
    {(showGuide || documents.length > 0) && <ResourceSection {...sectionProps("documents")} title="Documents" count={documents.length + Number(showGuide)}>
    {showGuide && <article className="deal-desk__resource-row">
      <div className="deal-desk__resource-name"><span className="deal-desk__resource-icon deal-desk__resource-icon--guide"><FiBookOpen aria-hidden="true" /></span>
        <div><h3>Riomax documentation</h3><p>Administrator guide and process reference</p></div></div>
      <span className="deal-desk__resource-type">Guide</span>
      <div className="deal-desk__resource-actions"><button type="button" className="deal-desk__button deal-desk__button--primary" onClick={() => { setDocumentLoading(true); setDocumentOpen(true); }}>Open</button>
        <a className="deal-desk__icon" href={documentationUrl} target="_blank" rel="noreferrer" title="Open documentation in new tab" aria-label="Open documentation in new tab"><FiExternalLink /></a></div>
    </article>}
    {documents.map(asset => renderResource(asset))}
    </ResourceSection>}
    {recordings.length > 0 && <ResourceSection {...sectionProps("recordings")} title="Recordings" count={recordings.length}>
      {recordingMonths.map(([key, month]) => <ResourceSection key={key} {...sectionProps(`month-${key}`)} nested title={month.title} count={month.assets.length}>
        {[...month.assets].sort((a, b) => (recordedDate(b)?.getTime() || 0) - (recordedDate(a)?.getTime() || 0)).map(asset => renderResource(asset, true))}
      </ResourceSection>)}
    </ResourceSection>}
    {other.length > 0 && <ResourceSection {...sectionProps("other")} title="Other resources" count={other.length}>
      {other.map(asset => renderResource(asset))}
    </ResourceSection>}
    </>}
    </div>
    {!loading && !error && !resultCount && <div className="deal-desk__empty"><FiFolder aria-hidden="true" /><h2>{emptyTitle}</h2>
      {hasFilters && <button type="button" className="deal-desk__button" onClick={() => { setQuery(""); setFilter("all"); setFormatFilter("all"); }}>Clear filters</button>}</div>}
  </section>;
}
